import { useCallback, useEffect, useRef, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import {
  confirmBulkResync,
  getBulkActiveJob,
  getBulkScanStatus,
  startBulkScan,
} from "../../utils/requests/subitemDiscrepancyRequests";
import {
  BulkActiveJob,
  BulkActiveJobResponse,
  BulkScanState,
  BulkScanStatusResponse,
  BulkSummary,
} from "./subitemBulk.types";

export type { BulkActiveJob, BulkScanState, BulkScanStatusResponse, BulkSummary };

/**
 * What a view that holds no job should do with the account's active job, as `/bulk/active`
 * reports it.
 *
 *  - `"adopt"`: take its jobId and let the existing status machine render it — the view is
 *    reattaching to a job it (or a previous instance of it) started on THIS board.
 *  - `"elsewhere"`: show it as the reason a scan cannot start here, by board and start time,
 *    and keep watching until it finishes.
 *  - `"ignore"`: behave as if there were no job.
 *
 * The board comparison is by string: `currentBoard` is a number everywhere in the page and the
 * record's `boardId` is the string the request boundary turned it into.
 */
export type ActiveJobDisposition = "adopt" | "elsewhere" | "ignore";

export const disposeActiveJob = (
  job: BulkActiveJob | null,
  currentBoard: number | null,
): ActiveJobDisposition => {
  if (!job || currentBoard === null) return "ignore";
  const sameBoard = String(job.boardId) === String(currentBoard);
  const running = job.status === "scanning" || job.status === "writing";

  // This board's job is adopted at EVERY stage but expiry. Scanning and writing are the
  // reattach case this hotfix exists for. A result awaiting confirmation is the one the user
  // paid twenty minutes for and would otherwise never see. `completed` and `failed` are kept
  // too, deliberately: the completed report is the only record of an irreversible write, and
  // the failure reason is the only explanation of a scan that died — both have their own exit
  // (Scan again / Try again), which dismisses the job so it is not adopted a second time.
  if (sameBoard) return job.status === "expired" ? "ignore" : "adopt";

  // Another board's job matters here only while it holds the lock. A finished job elsewhere
  // has nothing to do with this board and must not stop a scan from being offered.
  return running ? "elsewhere" : "ignore";
};

export const BULK_POLL_INTERVAL_MS = 2000;

/**
 * SWR's OWN default `dedupingInterval` is also 2000ms — identical to
 * `BULK_POLL_INTERVAL_MS`. Left at that default, a poll's revalidate can land
 * exactly at the tail end of the PREVIOUS fetch's dedup window and get
 * silently treated as a duplicate of it (no new network call, no state
 * change) rather than a real poll — the dedup window and the poll cadence
 * must never be equal, or every other poll races its own predecessor's
 * cooldown. Set comfortably below the poll interval so a poll's revalidate
 * is never mistaken for a dedupe of the fetch before it.
 */
const BULK_DEDUPING_INTERVAL_MS = 1000;

/**
 * Statuses after which nothing further will change without another user action.
 *
 * `awaiting_confirmation` STAYS in this set even now that 4b's confirm step
 * exists: nothing happens between the scan finishing and the user clicking
 * confirm, and that gap is however long they spend reading the summary
 * dialog — polling every `BULK_POLL_INTERVAL_MS` through it would be pure
 * waste. `confirm()` resumes polling itself, by revalidating the SWR key once
 * the confirm request resolves (see `confirm` below); the next status read
 * then returns `writing`, which is NOT in this set, so polling continues on
 * its own from there through to `completed`.
 */
/**
 * Key prefix of the subitem-scan cache entries a completed write invalidates.
 *
 * Must stay in step with `useSubitemDiscrepancy`'s own key
 * (`subitemDiscrepancy:${currentBoard}:${sortedIds}`). A prefix match rather than
 * an exact key on purpose: this hook does not know the parent-id page the list is
 * currently showing, and after a BOARD-WIDE write every cached page of it is
 * stale, not just the visible one.
 */
const SUBITEM_SCAN_KEY_PREFIX = "subitemDiscrepancy:";

const TERMINAL: ReadonlySet<string> = new Set([
  "awaiting_confirmation",
  "completed",
  "failed",
  "expired",
]);

/**
 * The one place the poll interval is computed. SWR's `refreshInterval` closure
 * and the `pollIntervalMs` this hook returns MUST call this same function
 * rather than each keeping their own copy of `TERMINAL.has(...) ? 0 : ...` —
 * `pollIntervalMs` exists so a test can assert polling stopped, and if it were
 * a second, independent computation, a future edit to just one of the two
 * copies would leave the OTHER (and its test) green while SWR itself kept
 * polling a finished job forever.
 */
const pollIntervalFor = (status?: string): number =>
  TERMINAL.has(status ?? "") ? 0 : BULK_POLL_INTERVAL_MS;

/**
 * A NUMBER is passed to SWR's `refreshInterval`, never a function — and this
 * replaces FIX 7, which was wrong in a way no test caught.
 *
 * FIX 7 hoisted `(latest) => pollIntervalFor(latest?.status)` to a stable
 * module-level reference, reasoning that SWR lists `refreshInterval` in its
 * polling effect's dependency array, so a fresh identity each render would tear
 * the timer down and rebuild it. That reasoning is correct and the conclusion
 * was backwards, because of how SWR actually drives the loop (v2.4.1,
 * dist/index/index.mjs:596-629):
 *
 *   function next() {
 *     const interval = isFunction(refreshInterval) ? refreshInterval(getCache().data) : refreshInterval;
 *     if (interval && timer !== -1) timer = setTimeout(execute, interval);   // <-- 0 => NO rearm
 *   }
 *   function execute() { ...revalidate(WITH_DEDUPE).then(next)... }
 *
 * The loop is self-perpetuating: each fetch's `.then(next)` arms the next timer.
 * So the FIRST time `next()` computes 0 — which is every time a poll lands on
 * `awaiting_confirmation` while the user reads the summary dialog — the chain
 * TERMINATES. Restarting it needs the effect itself to re-run, and its deps are
 * `[refreshInterval, refreshWhenHidden, refreshWhenOffline, key]`: the DATA is
 * not among them. A stable function identity therefore guarantees the effect
 * never re-runs, so `confirm()`'s `mutate()` could move the cached status to
 * `writing` and no timer would ever be armed again. The panel sat on "resyncing"
 * while the write ran, and finished, behind it — with `pollIntervalMs` reading a
 * perfectly healthy 2000 the whole time.
 *
 * A number fixes it precisely because numbers compare BY VALUE in that dep
 * array. It is stable across renders that do not change the status (so FIX 7's
 * actual concern — a torn-down timer on every parent re-render — still cannot
 * happen), and it changes exactly when polling must start or stop:
 * `0 -> 2000` re-runs the effect and arms a timer; `2000 -> 0` re-runs it and
 * leaves one unarmed.
 *
 * Still the one shared `pollIntervalFor` computation, for the reason its own
 * comment gives.
 */

/**
 * The one shared mapping from an HTTP status to the outcome it represents,
 * used by both `start()` and `confirm()` so the two can never drift apart —
 * a future edit to what 409 or 404 means for one of these requests only has
 * one place to change. Returns `null` for anything neither call site treats
 * as a recognized outcome, so ordinary errors still fall through to `failed`.
 *
 * The two callers do NOT honour the same subset of results, and that is
 * deliberate, not an oversight: `start()` only ever acted on 409 (a fresh
 * scan can't 404 — there's no existing job yet to have gone missing), while
 * `confirm()` acts on both. Sharing this function does not change that;
 * `start()` still ignores an `"expired"` result exactly as before.
 */
const mapBulkHttpError = (status: number | undefined): "busy" | "expired" | null => {
  if (status === 409) return "busy";
  if (status === 404) return "expired";
  return null;
};

/**
 * The companion to `mapBulkHttpError` for `/bulk/confirm`'s **HTTP 200**
 * outcomes, and the ONLY place a `busy` phase can come from on this endpoint.
 *
 * Note carefully that this is not the 200-shaped twin of that function: on
 * `/bulk/confirm` a 200 `{status: "busy"}` and a 409 mean different things —
 * the account lock is held, versus the record is no longer awaiting
 * confirmation — and only the former is a `busy` the user should be shown. See
 * `confirm()`'s catch block for the 409 side.
 *
 * `confirm` is the only request in this feature that reports a refusal INSIDE a
 * success envelope. `SubitemBulkService.confirm` answers
 * `success().setData({status: "busy"})` when the per-account single-flight lock
 * is held, and `{status: "expired"}` from three separate points where the job
 * record has vanished under its TTL. None of those are HTTP errors, so the
 * catch block cannot see any of them — which is exactly how the resolved body
 * came to be discarded and the consent button became a silent no-op.
 *
 * Returns `null` for `"writing"`, the one outcome that means "proceed": the
 * write is queued and the caller should revalidate so polling resumes. A
 * MISSING status is treated the same way — `response?.data?.data` is `any`, so
 * this can be reached with nothing in it, and revalidating (letting the polled
 * record answer) is the safe response to not knowing, where pinning a phase
 * guessed from an empty body is not.
 */
const mapBulkConfirmOutcome = (
  status: "writing" | "busy" | "expired" | undefined,
): "busy" | "expired" | null => (status === "writing" || status === undefined ? null : status);

/**
 * The ONLY confirm error status that is a definite statement that nothing was
 * written.
 *
 * `/bulk/confirm` is the one write-triggering request in this feature, and a
 * client timeout does not abort the upstream — `SubitemBulkService.confirm`
 * patches the record to `writing` and publishes to the queue whether or not
 * anyone is still listening for the reply. So the burden of proof runs the other
 * way here than it does for a read: an error is "nothing happened" only if the
 * endpoint answered in a way that proves it, and otherwise the outcome is
 * UNKNOWN.
 *
 * 400 proves it. It comes from the argument check at the very top of `confirm`,
 * before every guard, every lock and every publish. Nothing else does:
 * - 504 is simb-backend's client-timeout answer, whose own message says the
 *   resync may already have started.
 * - a missing `response` is a timeout or a dropped connection — the request may
 *   have been served in full and the reply lost.
 * - 500 is `confirm`'s own catch, which is reached from `sendMessage` throwing
 *   AFTER the record has already been patched to `writing`.
 *
 * 409 and 404 never reach this: `mapBulkHttpError` claims them first, and each
 * has a surface of its own.
 */
const isDefiniteConfirmRefusal = (status: number | undefined): boolean => status === 400;

/**
 * The backend's own error copy, preferred over axios's.
 *
 * Every simb-backend error answer on these routes carries `error_message`, and
 * on a confirm 504 that field is the whole point: the wording was chosen because
 * the outcome is unknown and tells the user where to look instead. Falling
 * through to `error.message` prints "Request failed with status code 504" at a
 * user, which says nothing and reads as a failure.
 *
 * Same accessor chain the rest of the app already uses for this envelope (see
 * ResynchronizeAllColumnsModal).
 */
const bulkErrorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.error_message ?? error?.message ?? fallback;

const useSubitemBulkScan = ({
  currentBoard,
  token,
}: {
  // `number | null`, matching SubitemsTable's prop and useSubitemDiscrepancy's
  // parameter. The board id is a number everywhere in the page and becomes a
  // string only at the request boundary, exactly as that hook does it.
  currentBoard: number | null;
  token: string | null;
}) => {
  const [jobId, setJobId] = useState<string | null>(null);
  const [startPhase, setStartPhase] = useState<"idle" | "starting" | "busy" | "failed">("idle");
  const [startReason, setStartReason] = useState<string | null>(null);
  // Mirrors `startPhase`/`startReason` for the confirm request. Kept as its own
  // pair of state rather than reusing `startPhase` because a confirm can only
  // ever happen once a job already exists (unlike start, which creates one) —
  // but the OUTCOMES it can produce are the same three the hook already has:
  // `busy` and `expired` are folded into the existing phases of those names in
  // `state` below rather than becoming a second, parallel set.
  //
  // Which HTTP result produces which is NOT the same as for `start()`, and the
  // difference matters: on `/bulk/confirm` the account lock is reported at HTTP
  // 200 in the body, while 409 means the record is no longer awaiting
  // confirmation. See `confirm()` below. Unlike `startPhase`, these are honoured
  // only while the polled record has not moved on — see `state`.
  const [confirmPhase, setConfirmPhase] = useState<
    "idle" | "confirming" | "busy" | "expired" | "failed" | "unknown"
  >("idle");
  const [confirmReason, setConfirmReason] = useState<string | null>(null);

  /**
   * The account's job when it is running on a DIFFERENT board — the reason a scan cannot start
   * here, as `/bulk/active` reported it. Rendered by the `busy` phase with the board named.
   */
  const [elsewhere, setElsewhere] = useState<BulkActiveJob | null>(null);

  /**
   * Jobs the user has explicitly walked away from with `reset`/`rescan`. The active-job lookup
   * re-arms the moment `jobId` is cleared (its SWR key depends on `!jobId`), and SWR answers it
   * from cache first — so without this, "Scan again" would clear the job and the lookup would
   * hand the very same finished job straight back, and the button would visibly do nothing. A
   * dismissed job is never adopted again by this view; a DIFFERENT job for the same board is.
   */
  const dismissedJobsRef = useRef<Set<string>>(new Set());

  // FIX 8: the hook otherwise has no idea the board underneath it changed.
  // `currentBoard` was in neither the SWR key nor `reset`'s dependencies, and
  // the response's own `boardId` was never compared — so switching boards
  // while a job for the PREVIOUS board was still running left that job's
  // summary rendering under the new board. Tracked with a ref (not state)
  // because this only needs to run a side effect on change, never trigger a
  // render of its own.
  const previousBoardRef = useRef(currentBoard);
  useEffect(() => {
    if (previousBoardRef.current === currentBoard) return;
    previousBoardRef.current = currentBoard;
    setJobId(null);
    setStartPhase("idle");
    setStartReason(null);
    setConfirmPhase("idle");
    setConfirmReason(null);
    setElsewhere(null);
  }, [currentBoard]);

  /**
   * The reattach lookup. Live exactly while this view holds NO job: that is the only time the
   * question "is there one I should be showing?" has a different answer from what the status
   * poll already knows. The key carries the board so a board switch asks afresh.
   *
   * Polls only while the answer is a job running on another board — the one case where the
   * panel is waiting on something it cannot otherwise observe (the lock is invisible to the
   * client, and that job's status poll belongs to whoever started it). Interval held in state
   * for the same reason `pollIntervalMs` below is: an inline function here would re-arm SWR's
   * timer on every render.
   */
  const [activePollMs, setActivePollMs] = useState<number>(0);
  const { data: active, mutate: mutateActive } = useSWR<BulkActiveJobResponse>(
    !jobId && token && currentBoard ? `subitemBulkActive:${currentBoard}` : null,
    () => getBulkActiveJob(token!),
    {
      refreshInterval: activePollMs,
      dedupingInterval: BULK_DEDUPING_INTERVAL_MS,
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );

  useEffect(() => {
    // `undefined` is "not answered yet"; `{ job: null }` is a real "nothing to attach to".
    if (active === undefined) return;
    const job = active.job;
    const disposition =
      job && dismissedJobsRef.current.has(job.jobId) ? "ignore" : disposeActiveJob(job, currentBoard);

    if (disposition === "adopt" && job) {
      setJobId(job.jobId);
      setStartPhase("idle");
      setStartReason(null);
      setElsewhere(null);
      setActivePollMs(0);
      return;
    }
    if (disposition === "elsewhere" && job) {
      setElsewhere(job);
      // The 409 that may have led here is now explained; the named job replaces the bare phase.
      setStartPhase((phase) => (phase === "busy" ? "idle" : phase));
      setActivePollMs(BULK_POLL_INTERVAL_MS);
      return;
    }
    setElsewhere(null);
    setActivePollMs(0);
  }, [active, currentBoard]);

  /**
   * The interval SWR is currently driven by. Held in state rather than derived
   * inline because it has to be readable BEFORE the `useSWR` call that produces
   * the status it is derived from; the effect below closes that loop one render
   * later, which is what makes the dep change and re-arm the timer.
   *
   * Starts at the polling interval, not 0: on mount there is no status yet, and
   * a job whose `jobId` has just been set must begin polling immediately.
   */
  const [pollIntervalMs, setPollIntervalMs] = useState<number>(BULK_POLL_INTERVAL_MS);

  const { data, error, mutate } = useSWR<BulkScanStatusResponse>(
    // `currentBoard` in the key as well as the effect above: belt and braces
    // against the same board-mismatch bug from the OTHER direction — even if
    // some future change left `jobId` populated across a board switch for a
    // moment, a key that already differs per board can never serve board A's
    // cached response to a board B render.
    jobId && token && currentBoard ? `subitemBulkScan:${currentBoard}:${jobId}` : null,
    () => getBulkScanStatus(token!, jobId!),
    {
      // A function, not a fixed number: SWR calls this with the LATEST cached
      // data after every fetch, so a finished job stops being requested. Passed
      // as the hoisted `refreshIntervalFor` reference (see its definition)
      // rather than an inline arrow here, which would get a new identity every
      // render and, per SWR's own polling effect dependency array, tear down
      // and restart the timer on every render.
      refreshInterval: pollIntervalMs,
      dedupingInterval: BULK_DEDUPING_INTERVAL_MS,
      revalidateOnFocus: false,
      // A 404 is a real terminal answer ("the record's TTL elapsed"), not a
      // transient fault to retry against.
      shouldRetryOnError: false,
    },
  );

  /**
   * Closes the loop: the status SWR just fetched decides whether it keeps
   * polling. Keyed on the STATUS rather than on `data` so an unchanged status
   * arriving in a new object identity does not restart the timer for no reason.
   */
  useEffect(() => {
    setPollIntervalMs(pollIntervalFor(data?.status));
  }, [data?.status]);

  /**
   * A finished write makes the discrepancy list underneath this panel WRONG, and
   * nothing else invalidates it: the panel reported success while every row still
   * read as drifting until the user reloaded.
   *
   * That list is `useSubitemDiscrepancy`'s SWR entry, owned by SubitemsTable — a
   * SIBLING of this panel. There is no shared `mutate` between them, and threading
   * one through the page would couple two components that otherwise know nothing
   * about each other, for a signal that is really "the server state changed". The
   * keys are namespaced strings precisely so they can be matched, so this
   * invalidates by prefix through SWR's global mutate.
   *
   * The `data?.status` dep is what makes this fire once per transition rather
   * than on every render: an unchanged status does not re-run the effect at all.
   *
   * The previous-status ref is therefore NOT what provides that, and is not
   * covered by the test below — deleting it leaves the suite green, checked by
   * mutation. It earns its place on the two paths the dep array cannot see: a
   * StrictMode double-invocation in development, and a re-run caused by
   * `globalMutate`'s identity changing while the status stands still. Both would
   * otherwise fire a second board-wide scan. Read it as belt-and-braces, not as
   * the mechanism.
   *
   * `failed` counts too, and that is not defensive: a run that dies partway has
   * still irreversibly written every batch before the failure, so its list is just
   * as stale as a completed one's. `expired` does not — the record aged out, which
   * says nothing about whether anything was written, and a scan cannot be provoked
   * by a job the user never confirmed.
   */
  const { mutate: globalMutate } = useSWRConfig();
  const previousStatusRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const previous = previousStatusRef.current;
    previousStatusRef.current = data?.status;
    if (previous === data?.status) return;
    if (data?.status !== "completed" && data?.status !== "failed") return;
    void globalMutate(
      (key) => typeof key === "string" && key.startsWith(SUBITEM_SCAN_KEY_PREFIX),
    );
  }, [data?.status, globalMutate]);

  /**
   * The scan request itself, with no opinion on whether starting one is allowed.
   *
   * `start` and `rescan` differ ONLY in that guard — one refuses while a job
   * exists, the other replaces it — so the request, its three outcomes and the
   * board/token check live here once rather than in two copies that could drift
   * on the next edit to what a 409 means.
   */
  const runScan = useCallback(async () => {
    if (!currentBoard || !token) return;

    setStartPhase("starting");
    setStartReason(null);

    try {
      const { jobId: startedJobId } = await startBulkScan(token, String(currentBoard));
      setJobId(startedJobId);
      setStartPhase("idle");
    } catch (startError: any) {
      // 409 is the single-flight lock doing its job, not a failure. A scan
      // request only ever acts on `"busy"` — see `mapBulkHttpError`'s comment
      // for why an `"expired"` result (which it will never produce here) is not
      // handled specially and simply falls through to `failed` below.
      if (mapBulkHttpError(startError?.response?.status) === "busy") {
        setStartPhase("busy");
        // Ask WHICH job holds the lock. On this board the effect above adopts it and the panel
        // shows the running scan instead of a refusal; on another board it names that board.
        // Best-effort: a lookup that fails or answers nothing leaves the bare `busy` phase.
        try {
          await mutateActive();
        } catch {
          // The 409 already stands on its own; the lookup only ever improves on it.
        }
        return;
      }
      setStartPhase("failed");
      setStartReason(startError?.message ?? "Could not start the scan");
    }
  }, [currentBoard, mutateActive, token]);

  const start = useCallback(async () => {
    if (jobId || startPhase === "starting") return;
    await runScan();
  }, [jobId, runScan, startPhase]);

  /**
   * Replace the job this hook is holding with a brand new scan, in one action.
   *
   * The control for the surfaces where the CURRENT job has nothing left to give:
   * `expired` (its snapshot is gone) and `completed` (its write is done). Both
   * used to be dead ends — `state` cannot return to `idle` while `jobId` is set,
   * so with no control on those branches, and this component being the feature's
   * only surface, a page reload was the sole way to scan again.
   *
   * `reset()` is not that control and `reset()` + `start()` cannot be either:
   * `start` refuses outright while `jobId` is set, and `reset`'s state update is
   * not visible to a `start` call in the same handler, so the pair silently does
   * nothing. Hence one callback that drops the job and issues the request itself.
   *
   * `reset` remains the right control where returning to the idle strip is not a
   * dead end — `failed`'s "Try again" and the `ready` surface's scan_failed
   * retry, both of which sit on a panel the user can still act on.
   */
  const rescan = useCallback(async () => {
    if (startPhase === "starting") return;
    // The old job's outcome must not survive its record: a stale confirm phase
    // would otherwise be honoured against the NEW job (see `state`, where those
    // phases are gated only on the polled record still awaiting confirmation).
    setConfirmPhase("idle");
    setConfirmReason(null);
    if (jobId) dismissedJobsRef.current.add(jobId);
    setJobId(null);
    await runScan();
  }, [jobId, runScan, startPhase]);

  const confirm = useCallback(async () => {
    if (!jobId || !token || confirmPhase === "confirming") return;

    setConfirmPhase("confirming");
    setConfirmReason(null);

    try {
      const outcome = await confirmBulkResync(token, jobId);
      // The resolved body is an OUTCOME, not an acknowledgement. Discarding it
      // (as this did) made the button a silent no-op for the two refusals the
      // endpoint reports at HTTP 200 — see `mapBulkConfirmOutcome`. Both drive
      // the very same phases their HTTP-error counterparts drive in the catch
      // below, through the same one-line shape, so a 200 `busy` and a 409 can
      // never diverge in what the user is told.
      const settled = mapBulkConfirmOutcome(outcome?.status);
      if (settled) {
        setConfirmPhase(settled);
        return;
      }

      setConfirmPhase("idle");
      // `awaiting_confirmation` is terminal (see TERMINAL's comment above), so
      // nothing will refetch on its own once the write starts. This revalidate
      // is what turns the NEXT status read into `writing` — not terminal — so
      // SWR's own `refreshInterval` takes over from there through to
      // `completed`, exactly the way `start()` hands off to the SWR key once
      // `jobId` is set.
      await mutate();
    } catch (confirmError: any) {
      // Still the same `mapBulkHttpError` `start()` uses — the HTTP numbers 409
      // and 404 appear in exactly one place in this file — but `confirm()` does
      // not resolve its "busy" result the way `start()` does, and that is the
      // point of the split below rather than an oversight.
      //
      // For `start()`, 409 IS the account lock: "a bulk scan is already running
      // for this account", and there is no record to consult because the job was
      // never created. For `confirm()` the account lock answers HTTP 200
      // `{status: "busy"}` instead (handled above), and 409 comes from a
      // different guard entirely — the record exists but its status is not
      // `awaiting_confirmation`, i.e. it is already writing, or completed, or
      // failed. Reporting that as "a scan is already running" is wrong about a
      // job that has usually already finished, so the record is asked instead of
      // guessed at: revalidate, and let the polled status resolve to the real
      // `writing`/`completed`/`failed` phase, each of which has its own surface.
      const mapped = mapBulkHttpError(confirmError?.response?.status);
      if (mapped === "busy") {
        setConfirmPhase("idle");
        await mutate();
        return;
      }
      if (mapped === "expired") {
        setConfirmPhase("expired");
        return;
      }

      // Everything left is an error the endpoint did not turn into a definite
      // "nothing happened" — see `isDefiniteConfirmRefusal`. This request starts
      // an IRREVERSIBLE write, so the two branches below are not two flavours of
      // the same thing: `failed` asserts the boards were not touched, and
      // asserting that while a publish is landing is the worst thing this
      // feature can say.
      if (!isDefiniteConfirmRefusal(confirmError?.response?.status)) {
        setConfirmPhase("unknown");
        setConfirmReason(
          bulkErrorMessage(
            confirmError,
            "The resync may already have started. Check the progress view rather than retrying confirmation.",
          ),
        );
        // The record is the ONLY thing that can resolve this, and it is not
        // polling: `awaiting_confirmation` is terminal and `revalidateOnFocus`
        // is false, so without this the panel would sit on an unknown outcome
        // that nothing would ever refetch. `confirm` patches the record to
        // `writing` BEFORE it publishes, so where the write did start this
        // revalidate is what lets the record win over the unknown outcome (see
        // `state`) and shows the user their own write progressing.
        await mutate();
        return;
      }

      setConfirmPhase("failed");
      setConfirmReason(bulkErrorMessage(confirmError, "Could not confirm the write"));
    }
  }, [confirmPhase, jobId, mutate, token]);

  const reset = useCallback(() => {
    if (jobId) dismissedJobsRef.current.add(jobId);
    setJobId(null);
    setStartPhase("idle");
    setStartReason(null);
    setConfirmPhase("idle");
    setConfirmReason(null);
    setElsewhere(null);
  }, [jobId]);

  /**
   * The way out of a `busy` or `unknown` panel — and deliberately NOT `reset`.
   *
   * A confirm refused for the account lock (HTTP 200 `{status: "busy"}`) leaves
   * this job's own record untouched on `awaiting_confirmation`, which is
   * TERMINAL: polling has already stopped, so nothing will refetch on its own
   * and the panel has no way to learn that the other account job has since
   * finished. Before this existed, `busy` was the only non-transient surface in
   * the panel with no control on it at all, and a page reload was the only exit.
   *
   * Clears the resolved request's outcome and revalidates, but KEEPS `jobId`, so
   * a perfectly good scan summary the user already waited for is not thrown away
   * and re-earned — they land back on `ready` with the same summary and can
   * simply confirm again. `reset()` remains the control for the surfaces where
   * the scan itself is what needs redoing.
   *
   * Clears BOTH phases rather than just the confirm one, because `busy` has two
   * origins: `start()`'s 409 (no `jobId` at all, so this behaves exactly like
   * `reset` there) and `confirm()`'s 200 body. One control has to answer for
   * whichever pinned the panel, or it would visibly do nothing for the other.
   *
   * It is also the ONLY control the `unknown` surface may offer, for a stronger
   * version of the same reason. There, `jobId` is the sole route to the record of
   * a write that may be running right now on the user's boards, with no undo and
   * no other surface in the product that reports it — `reset()` would discard
   * exactly the thing the user needs. Re-reading the record is both the escape
   * and the answer.
   */
  const recheck = useCallback(async () => {
    setStartPhase("idle");
    setStartReason(null);
    setConfirmPhase("idle");
    setConfirmReason(null);
    // Both: the status poll for a job this view holds, and the lookup for one it does not. Whichever
    // is live answers; the other has a null key and is a no-op.
    await Promise.all([mutate(), mutateActive()]);
  }, [mutate, mutateActive]);

  const state = ((): BulkScanState => {
    // `start()`'s outcomes are unconditional: it failed to CREATE a job, so
    // there is no record to consult and nothing for a poll to override.
    if (startPhase === "busy") return { phase: "busy" };
    if (startPhase === "failed") return { phase: "failed", reason: startReason ?? "Unknown error" };
    if (startPhase === "starting") return { phase: "starting" };
    if (!jobId && elsewhere) {
      return {
        phase: "busy",
        elsewhere: {
          jobId: elsewhere.jobId,
          boardId: String(elsewhere.boardId),
          boardName: elsewhere.boardName,
          startedAt: elsewhere.createdAt,
          progress: elsewhere.progress,
        },
      };
    }
    if (!jobId) return { phase: "idle" };

    /**
     * A confirm outcome describes a request that has ALREADY resolved; the
     * polled record describes what is true now. Where the two disagree, the
     * record wins — so a confirm outcome is honoured only while the record has
     * not moved on from `awaiting_confirmation`, i.e. only while nothing has
     * actually happened for it to be wrong about.
     *
     * This ordering is the fix, not a tidy-up. `confirmPhase === "busy"` used to
     * be evaluated first and unconditionally, which made a single refused
     * confirm permanently override everything every subsequent poll learned:
     * a job that completed in another tab, or this tab's own write once another
     * tab's confirm won the lock, both stayed hidden behind a stale "a scan is
     * already running" until the page was reloaded.
     *
     * `!data` counts as "not moved on": with nothing polled back there is no
     * record to prefer, so the confirm outcome is all there is to go on.
     */
    const recordStillAwaitingConfirmation = !data || data.status === "awaiting_confirmation";
    if (recordStillAwaitingConfirmation) {
      if (confirmPhase === "busy") return { phase: "busy" };
      if (confirmPhase === "failed") {
        return { phase: "failed", reason: confirmReason ?? "Unknown error" };
      }
      if (confirmPhase === "expired") return { phase: "expired" };
      // Subordinate to the record for the same reason as the rest, and it
      // matters most here: the whole point of an unknown outcome is that the
      // record might know. `confirm` patches `writing` before it publishes, so a
      // record that has moved on has ANSWERED the question, and the user should
      // see their write rather than a warning about it.
      if (confirmPhase === "unknown") {
        return { phase: "unknown", reason: confirmReason ?? "The write's outcome is unknown" };
      }
    }

    // An expired record answers 404. Distinguishing it from "still scanning" is
    // what stops the UI spinning forever on a job that no longer exists.
    if (error?.response?.status === 404 || data?.status === "expired") return { phase: "expired" };
    if (error) return { phase: "failed", reason: error?.message ?? "Could not read the scan" };
    if (data?.status === "failed") {
      // `data.report` carried through, not dropped. The write job patches its
      // running report onto the record along WITH the failure
      // (subitem-bulk-resync.job.ts's failure patch sends `{status: "failed",
      // failureReason, report, cursor}`), so a job that threw at batch 31 of 120
      // has a truthful account of the 30 batches that already landed — and those
      // cells are changed on the user's boards with no undo. Reporting only the
      // reason left that account unreachable from anywhere in the product.
      //
      // Passed through as-is, including `undefined`: a scan-stage failure never
      // wrote anything, and the absence is what tells the surface not to claim
      // a partial write.
      return {
        phase: "failed",
        reason: data.failureReason ?? "The scan failed",
        report: data.report,
      };
    }
    if (data?.status === "writing") {
      // Same "terminal-without-summary" guard as below, applied here too: a
      // `writing` status with no summary would otherwise fall through to the
      // generic `scanning` return, stranding the UI on a spinner that reads as
      // "still scanning" for a write that is, in fact, already running.
      if (!data.summary) {
        return { phase: "failed", reason: "The write started without a summary" };
      }
      // `?? 0` rather than optional: the write is running, so "no cursor yet"
      // is genuinely zero progress (the record carries none until the first
      // batch patches one) and the panel should say 0%, not hide the measure.
      return { phase: "writing", jobId, summary: data.summary, cursor: data.cursor ?? 0 };
    }
    if (data && TERMINAL.has(data.status)) {
      // A terminal status with no summary is not "still scanning" — polling has
      // already stopped (pollIntervalFor returns 0 for every TERMINAL status),
      // so leaving this as `scanning` would strand the UI on a live-looking
      // spinner for a job that will never be polled again.
      if (!data.summary) {
        return { phase: "failed", reason: "The scan finished without a summary" };
      }
      if (data.status === "completed") {
        if (!data.report) {
          return { phase: "failed", reason: "The write finished without a report" };
        }
        return { phase: "completed", jobId, summary: data.summary, report: data.report };
      }
      return { phase: "ready", jobId, summary: data.summary, finishedAt: data.finishedAt };
    }
    return { phase: "scanning", jobId, startedAt: data?.createdAt, progress: data?.progress };
  })();

  return {
    state,
    start,
    confirm,
    reset,
    recheck,
    rescan,
    refresh: mutate,
    // Exposed so a test can assert polling actually stopped rather than
    // inferring it from a call count that a fake timer might not have advanced.
    // Shares `pollIntervalFor` with the SWR `refreshInterval` closure above —
    // see that function's comment for why this must not be a second, separate
    // computation.
    pollIntervalMs: pollIntervalFor(data?.status),
  };
};

export default useSubitemBulkScan;
